import { ArrowRightIcon, EquipmentIcon, ShirtIcon, UserAddIcon } from "@xola/icons";
import { isEquipmentItem } from "common";
import { isArray, uniqueId } from "lodash";
import React from "react";
import { Audit, LineItemType, Purchase, PurchaseItem } from "types";
import { TimelineItem, TimelinePill } from "../TimelineItem";
import { isMerchandiseItem } from "@/models/PurchaseItem";

interface ChangeQuantityItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const ChangeQuantityItem = ({ purchase, item, audit, showDate = false }: ChangeQuantityItemProps) => {
    const [from, to] = getQuantityChanges(item, audit);

    const isMerchItem = isMerchandiseItem(item);
    const isEquipmentChange = isEquipmentItem(item) && hasEquipmentLineItemChanges(item, audit);
    const title = isMerchItem ? "Changed Quantities" : isEquipmentChange ? "Equipment Changed" : "Guests Changed";

    if (!ChangeQuantityItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return (
        <TimelineItem
            title={title}
            audit={audit}
            icon={isEquipmentChange ? <EquipmentIcon /> : <UserAddIcon />}
            color="primary"
            showDate={showDate}
            purchase={purchase}
        >
            <div>
                <div className="flex items-center space-x-4">
                    <TimelinePill>
                        <span className="flex flex-col space-y-2" data-testid="change-quantity-item-guest-from">
                            {from.map((from) => (
                                <span key={uniqueId("from-")} className="flex max-w-75 items-start">
                                    {isMerchItem && <ShirtIcon className="mr-1 mt-0.5 shrink-0" />}
                                    <var
                                        data-var="changedQuantityFrom"
                                        className="whitespace-normal break-words not-italic"
                                    >
                                        {from}
                                    </var>
                                </span>
                            ))}
                        </span>
                    </TimelinePill>

                    <ArrowRightIcon />

                    <TimelinePill>
                        <span className="flex flex-col space-y-2" data-testid="change-quantity-item-guest-to">
                            {to.map((to) => (
                                <span key={uniqueId("to-")} className="flex max-w-75 items-start">
                                    {isMerchItem && <ShirtIcon className="mr-1 mt-0.5 shrink-0" />}
                                    <var
                                        data-var="changedQuantityTo"
                                        className="whitespace-normal break-words not-italic"
                                    >
                                        {to}
                                    </var>
                                </span>
                            ))}
                        </span>
                    </TimelinePill>
                </div>
            </div>
        </TimelineItem>
    );
};

ChangeQuantityItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    const [from, to] = getQuantityChanges(item, audit);
    return from.length > 0 && to.length > 0;
};

const getQuantityChanges = (item: PurchaseItem, audit: Audit) => {
    const auditItems: any[] = isArray(audit.data?.items) ? audit.data?.items : Object.values(audit.data?.items ?? {});
    const isMerchItem = isMerchandiseItem(item);

    const matchingAuditItem: any = auditItems.find((auditItem: any) => {
        return auditItem.id === item?.id && (!!auditItem.quantity || hasGuestChanges(auditItem.guests));
    });

    const oldDemographics: string[] = [];
    const newDemographics: string[] = [];

    if (matchingAuditItem?.lineItems) {
        for (const lineItem of Object.values(matchingAuditItem.lineItems) as any) {
            if (lineItem.quantity) {
                // Existing line item with quantity changed
                const demographicLineItem = item.lineItems.find(({ id }) => id === lineItem.id);
                if (isMerchItem && demographicLineItem?.type === LineItemType.SUBTOTAL) {
                    continue;
                }
                const displayLineItem = demographicLineItem ?? { name: isEquipmentItem(item) ? "Equipment" : "Guest" };
                oldDemographics.push(`${lineItem.quantity.old} x ${displayLineItem.name}`);
                newDemographics.push(`${lineItem.quantity.new} x ${displayLineItem.name}`);
            } else if (lineItem.old === null && lineItem.new?.quantity) {
                // Switching to a type the purchase never had adds a line item rather than changing a
                // quantity, so it has no `quantity` delta to read — take the count off the new item.
                const newLineItem = item.lineItems.find(({ id }) => id === lineItem.new.id) ?? lineItem.new;
                oldDemographics.push(`0 x ${newLineItem.name}`);
                newDemographics.push(`${lineItem.new.quantity} x ${newLineItem.name}`);
            }
        }

        return [oldDemographics, newDemographics];
    }

    return [[], []];
};

const hasEquipmentLineItemChanges = (item: PurchaseItem, audit: Audit): boolean => {
    const auditItems: any[] = isArray(audit.data?.items) ? audit.data?.items : Object.values(audit.data?.items ?? {});
    const matchingAuditItem: any = auditItems.find((auditItem: any) => auditItem.id === item?.id);
    if (!matchingAuditItem?.lineItems) return false;
    return Object.values(matchingAuditItem.lineItems).some((lineItem: any) => {
        if (!lineItem.quantity) return false;
        const matched = item.lineItems.find(({ id }) => id === lineItem.id);
        return matched?.type === LineItemType.EQUIPMENT;
    });
};

const hasGuestChanges = (guests: any): boolean => {
    const guestValues = Object.values(guests ?? {});
    if (guestValues.length === 0) return false;

    return guestValues.some((guest: any) => {
        const target = guest.old !== undefined ? guest.new ?? guest.old : guest;

        return ["name", "template", "traveler", "participant"].some((key) => target?.[key] !== undefined);
    });
};
